import { useState, useEffect } from "react";
import { Button } from "./ui-common/button";
import { Label } from "./ui-common/label";
import { Checkbox } from "./ui-common/checkbox";
import { Card, CardContent, CardHeader } from "./ui-common/card";

const LoginPage = ({ clientId, onGoogleLogin }) => {
  const [isConsentGiven, setIsConsentGiven] = useState(false);

  useEffect(() => {
    // to retain the state on refresh/new visit, if consent was already given
    if (localStorage.getItem("disclaimerRead") === "true") {
      setIsConsentGiven(true);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!isConsentGiven) {
      alert("Please agree to the consent terms to continue.");
      return;
    }

    // Proceed with Google OAuth
    onGoogleLogin();
  };

  const startGoogleLogin = () => {
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: window.location.origin + "/",
      response_type: "code",
      scope: "openid profile email",
      hd: "mail.tau.ac.il",
      access_type: "offline",
      prompt: "consent",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/20 p-4">
      <Card className="w-full max-w-md border-2 border-gray-300 shadow-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-8">
            <img 
              src="/logo color TM.svg"
              alt="Study Buddy"
              className="h-16 w-auto"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Disclaimer Section */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Please read the Terms & Consent</Label>
              <div
                className="h-32 w-full border-2 border-gray-400 rounded-md p-3 overflow-y-auto shadow-inner bg-white"
              >
                <div className="text-xs text-muted-foreground space-y-2">
                  <p className="font-medium">Study Buddy – Terms & Consent</p>
                  
                  <p className="font-medium">1. Introduction</p>
                  <p>
                    Study Buddy is an AI-based educational assistant designed to support your learning by providing explanations, guidance, and interactive study tools based on the materials of your course (the "Services"). Study Buddy does not replace attending classes, studying course materials, or consulting instructors. Responses may sometimes be incomplete, simplified, or inaccurate. Always verify important content using official course materials and academic guidance.
                  </p>
                  <p>
                    By using Study Buddy, you acknowledge that the system is a supplementary learning tool and not a replacement for course content or instruction.
                  </p>
                  
                  <p className="font-medium">2. Acceptance of Terms</p>
                  <p>
                    By logging in, accessing, or using the Services, you confirm that you have read, understood, and agree to these Terms & Consent (the "Terms"). If you do not agree, you must not use the Services.
                  </p>
                  <p>
                    Study Buddy may update these Terms periodically. Continued use constitutes acceptance of any updates.
                  </p>
                  
                  <p className="font-medium">3. Eligibility</p>
                  <p>
                    The Services are intended for use by enrolled students and authorized academic staff. By using the Services, you confirm that:
                  </p>
                  <p>
                    • You are authorized by your institution to access this system.<br/>
                    • You will use the Services solely for academic and lawful purposes.<br/>
                    • You will uphold all academic integrity expectations of your institution.
                  </p>
                  
                  <p className="font-medium">4. User Account</p>
                  <p>
                    Your access may be provided automatically through your institution. You are responsible for:
                  </p>
                  <p>
                    • Keeping your login credentials private.<br/>
                    • Ensuring no one else uses your account.<br/>
                    • Reporting any suspected unauthorized access.
                  </p>
                  <p>
                    Your account may be suspended or terminated if misuse is suspected.
                  </p>
                  
                  <p className="font-medium">5. Permitted Use</p>
                  <p>
                    Study Buddy is intended to help you clarify course topics, review what was covered in class, and support your own study process.
                  </p>
                  <p>
                    You may not:
                  </p>
                  <p>
                    • Use Study Buddy to complete assignments, exams, or assessments dishonestly.<br/>
                    • Attempt to trick the system into producing prohibited content.<br/>
                    • Copy, export, or redistribute the system's responses commercially.
                  </p>
                  
                  <p className="font-medium">6. Privacy</p>
                  <p>
                    Your questions are private - neither instructors nor teaching assistants see the questions, the answers, or who asked them.
                  </p>
                  <p>
                    Data may be used in anonymous, aggregated form to improve the system and support teaching (for example, identifying topics that many students find challenging).
                  </p>
                  
                  <p className="font-medium">7. No Upload of Personal Data</p>
                  <p>
                    Do not enter into Study Buddy:
                  </p>
                  <p>
                    • Personal data of yourself or others beyond what is necessary to log in<br/>
                    • Identification numbers, phone numbers, addresses, medical data, or similar
                  </p>
                  <p>
                    Study Buddy assumes no responsibility for any personal information you voluntarily share in violation of this policy.
                  </p>
                  
                  <p className="font-medium">8. Third-Party Services</p>
                  <p>
                    Study Buddy uses third-party providers (e.g., LLM used by Study buddy, AWS, Azure) to deliver AI responses and system functionality. These providers may process data according to their own terms and privacy policies.
                  </p>
                  <p>
                    Study Buddy is not responsible for outages, errors, or performance issues originating from third-party services.
                  </p>
                  
                  <p className="font-medium">9. Important Limitations</p>
                  <p>
                    Study Buddy may occasionally:
                  </p>
                  <p>
                    • Provide incomplete or inaccurate responses<br/>
                    • Omit context<br/>
                    • Produce simplified interpretations
                  </p>
                  <p>
                    You remain responsible for verifying all academic information. Always confirm key concepts and details using:
                  </p>
                  <p>
                    • Course lectures<br/>
                    • Official reading materials<br/>
                    • Instructor advice
                  </p>
                  <p>
                    Your academic performance and decisions are your responsibility.
                  </p>
                  
                  <p className="font-medium">10. System Availability and Fair Use</p>
                  <p>
                    Study Buddy may monitor usage volume to ensure fair access for all users. Study Buddy may modify, limit, suspend, or discontinue the Services at any time.
                  </p>
                  
                  <p className="font-medium">11. Disclaimer of Warranties</p>
                  <p>
                    The Services are provided "as-is", without warranties of any kind, including accuracy, reliability, or suitability for a particular purpose. Study Buddy does not guarantee uninterrupted or error-free operation.
                  </p>
                  
                  <p className="font-medium">12. Limitation of Liability</p>
                  <p>
                    To the fullest extent permitted by law, Study Buddy is not liable for:
                  </p>
                  <p>
                    • Academic outcomes<br/>
                    • Assignment or exam results<br/>
                    • Decisions made based on the system's responses<br/>
                    • Loss of data or system availability
                  </p>
                  
                  <p className="font-medium">13. Governing Law</p>
                  <p>
                    These Terms are governed by the laws of the State of Israel. Any disputes shall be resolved in the competent courts of Tel Aviv, Israel.
                  </p>
                  
                  <p className="font-medium">14. Contact</p>
                  <p>
                    For questions or clarifications:<br/>
                    Email: support@studybuddy.study
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="consent"
                checked={isConsentGiven}
                onCheckedChange={(checked) => {
                  setIsConsentGiven(checked);
                  if (checked) {
                    localStorage.setItem("disclaimerRead", "true");
                  }
                }}
              />
              <Label htmlFor="consent" className="text-sm">
                I have read and agree to the Terms & Consent
              </Label>
            </div>

            <Button 
              type="button"
              className="w-full" 
              disabled={!isConsentGiven}
              onClick={startGoogleLogin}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;